import { IBrushType, PenEventName } from "../enums";
import { EventDispatcher } from "../event";
import { NEO_SMARTPEN_TYPE } from "../neopen/INeoSmartpen";
import { isPlatePaper } from "../noteserver/SurfaceInfo";
import { NeoStroke, StrokeStatus } from "../structures";
import { getPageSOBP, makeNPageIdStr } from "../util/functions";
/** @type {InkStorage} */
let _storage_instance = null;
let _storage_2nd_instance = null;
export var InkStorageEventName;
(function (InkStorageEventName) {
    InkStorageEventName["ON_PAGE_ADDED"] = "is_page_added";
    InkStorageEventName["ON_PAGE_REMOVED"] = "is_page_removed";
    InkStorageEventName["ON_PEN_ADDED"] = "is_pen_added";
    /**
     * 단위 stroke가 지워진 경우
     * 보내지는 event의 내용물은 지워진 처리가 된 NeoStroke의 배열
     * 2021/06/09 추가
     */
    InkStorageEventName["ON_STROKE_ERASED"] = "stroke_erased";
    InkStorageEventName["ON_STROKE_ADDED"] = "stroke_added";
    InkStorageEventName["ON_STROKE_ERASED_TO_REMOTE"] = "stroke_erased_to_be_sent";
    InkStorageEventName["ON_OWNER_ADDED"] = "owner_added";
    InkStorageEventName["ON_WRITER_ADDED"] = "writer_added";
    // 2021/07/06
    InkStorageEventName["ON_NETWORK_STROKE_ADDED"] = "nw_stroked_added";
    //2022/08/16 - june
    InkStorageEventName["ON_INKSTORE_STROKE_LOADED"] = "on_inkstore_stroke_loaded";
})(InkStorageEventName || (InkStorageEventName = {}));
export default class InkStorage {
    getPenList() {
        return this._penUIDs;
    }
    get writerIds() { return this._writerIds; }
    get surfaceOwnerIds() { return this._surfaceOwnerIds; }
    /** @type {InkStorage} */
    // static instance;
    constructor(name) {
        // 2021/12/27
        this.strokes = [];
        /** strokeKey ==> Stroke */
        this.completed = new Map(); // completed strokes
        /** originalKey ==> Stroke */
        this.originalKeyStrokes = new Map(); // originalKey completed strokes
        /** sourceKey ("uuid" ) ==> Stroke */
        this.realtime = new Map(); // realtime strokes (incompleted strokes)
        /** (pageId (s.o.b.p)) ==> ({mac : NeoStroke[]}) */
        this.completedOnPage = new Map();
        /** (pageId (s.o.b.p)) ==> NeoStroke */
        this.pageStartStroke = new Map();
        this.pageEndStroke = new Map();
        /** (pageId) ==> ({strokeKey : NeoStroke}) */
        this.realtimeOnPage = new Map();
        this.dispatcher = new EventDispatcher();
        this.lastPageInfo = { section: -1, book: -1, owner: -1, page: -1 };
        this._surfaceOwnerIds = [];
        this._writerIds = [];
        /** 스트로크를 한번이라도 등록한 적이 있는 펜 리스트 */
        this._penUIDs = [];
        /**
         * register event handler, following events will be dispatched from this storage
         *   1) PenEventName.ON_PEN_DOWN
         *   2) PenEventName.ON_PEN_PAGEINFO
         *   3) PenEventName.ON_PEN_MOVE
         *   4) PenEventName.ON_PEN_UP
         *   5) InkStorageEventName.ON_PAGE_ADDED
         *   6) InkStorageEventName.ON_PAGE_REMOVED
         *   7) InkStorageEventName.ON_PEN_ADDED
         *
         * @param {PenEventName} eventName
         * @param {(event: IPenToViewerEvent) => void} listener
         * @param {*} filter
         * @memberof InkStorage
         */
        this.addEventListener = (eventName, listener, filter) => {
            this.dispatcher.on(eventName, listener, filter);
            // console.log("bound", listener);
        };
        /**
         *
         * @param eventName
         * @param listener
         */
        this.removeEventListener = (eventName, listener) => {
            this.dispatcher.off(eventName, listener);
        };
        /**
         * get strokes in a certain page, not including 'realtime state' strokes
         *
         * @param {IPageSOBP} pageInfo
         * @return {*}  {NeoStroke[]}
         * @memberof InkStorage
         */
        this.getPageStrokes = (pageInfo) => {
            const { section, owner, book, page } = pageInfo;
            const pageId = makeNPageIdStr({ section, owner, book, page });
            const arr = this.completedOnPage.get(pageId);
            if (arr)
                return arr;
            return [];
        };
        /**
         * get pageIds from this ink storage
         *
         * @return string[]
         * @memberof InkStorage
         */
        this.getPageList = () => {
            return Array.from(this.completedOnPage.keys()).map(pageId => getPageSOBP(pageId));
        };
        /**
         * completely remove stokres on a page
         *
         * @param {IPageSOBP} pageInfo
         * @memberof InkStorage
         */
        this.removeStrokeFromPage = (pageInfo) => {
            const { section, owner, book, page } = pageInfo;
            const pageId = makeNPageIdStr({ section, owner, book, page });
            this.completedOnPage.delete(pageId);
        };
        /**
         * get 'realtime state' strokes on page
         *
         * @param {IPageSOBP} pageInfo
         * @return {*}  {NeoStroke[]}
         * @memberof InkStorage
         */
        this.getPageStrokes_live = (pageInfo) => {
            const { section, owner, book, page } = pageInfo;
            const pageId = makeNPageIdStr({ section, owner, book, page });
            /** @type {Map.<string, Map>} - (pageId) ==> (strokeKey ==> NeoStroke) */
            const realtime = this.realtimeOnPage;
            /** @type {Map.<string, NeoStroke>} - (strokeKey) ==> (NeoStroke) */
            const penStrokesMap = realtime.get(pageId);
            if (penStrokesMap) {
                /** @type {Array.<NeoStroke>} */
                const arr = [];
                penStrokesMap.forEach((value) => {
                    arr.push(value);
                });
                return arr;
            }
            return [];
        };
        /**
         * mark a stokre as 'removed'
         *
         * @param {NeoStroke} stroke
         * @memberof InkStorage
         */
        this.markErased = (stroke) => {
            stroke.status = StrokeStatus.ERASED;
        };
        /**
         *
         */
        this.getLastPageInfo = () => {
            return this.lastPageInfo;
        };
        /**
         * Append a dummy page on ink storage
         *
         * @param {IPageSOBP} pageInfo
         * @return boolean
         * @memberof InkStorage
         */
        this.addDummyPage = (pageInfo) => {
            const pageId = makeNPageIdStr(pageInfo);
            if (!this.completedOnPage.has(pageId)) {
                this.completedOnPage.set(pageId, new Array(0));
                // hand the event
                this.dispatcher.dispatch(InkStorageEventName.ON_PAGE_ADDED, {
                    inputType: NEO_SMARTPEN_TYPE.MENU_ACTION,
                    strokeKey: "",
                    mac: "",
                    pageInfos: [pageInfo],
                    surfaceOwnerId: null,
                    writerId: null,
                });
                return true;
            }
            return false;
        };
        /**
         * append a stroke to 'completed state' storage
         *    internal operation will be done like the below
         *      1) Create a stroke
         *      2) add the stroke to 'realtime state' storage
         *      3) processing page_info -> page_move -> pen_move -> ... -> pen_up
         *      4) remove the stroke from 'realtime state' storage
         *      5) append the stroke to 'completed state' storage, and remove it from 'realtime state' storage
         *it
         * @private
         * @param {NeoStroke} stroke
         * @memberof InkStorage
         */
        this.addCompletedToPage = (stroke) => {
            const { section, owner, book, page } = stroke;
            const pageId = makeNPageIdStr({ section, owner, book, page });
            // console.log( `add completed: ${mac},  ${pageId} = ${section}.${book}.${owner}.${page} `);
            // stroke에 점이 하나라도 있어야 옮긴다.
            if (stroke.dotArray.length > 0) {
                // 배열이 없으면 만들어 준다.
                if (!this.completedOnPage.has(pageId)) {
                    this.completedOnPage.set(pageId, new Array(0));
                    // hand the event
                    this.dispatcher.dispatch(InkStorageEventName.ON_PAGE_ADDED, {
                        inputType: stroke.inputType,
                        strokeKey: stroke.key,
                        mac: stroke.mac,
                        pageInfos: [{ section, owner, book, page }],
                        surfaceOwnerId: stroke.surfaceOwnerId,
                        writerId: stroke.writerId,
                    });
                }
                // 배열에 넣는다.
                /** @type {Array.<NeoStroke>} */
                const arr = this.completedOnPage.get(pageId);
                arr.push(stroke);
                // 2021/12/27
                this.strokes.push(stroke);
                this.lastPageInfo = { section, owner, book, page };
                // console.log(completed);
            }
        };
        /**
         * add a stroke to 'realtime state' storage
         *
         * @private
         * @param {NeoStroke} stroke
         * @memberof InkStorage
         */
        this.addRealtimeToPage = (stroke) => {
            const { section, owner, book, page, key } = stroke;
            const pageId = makeNPageIdStr({ section, owner, book, page });
            /** @type {Map.<string, Map>} - (pageId) ==> (strokeKey ==> NeoStroke) */
            const realtime = this.realtimeOnPage;
            if (!realtime.has(pageId))
                realtime.set(pageId, new Map());
            /** @type {Map.<string, NeoStroke>} - (strokeKey) ==> (NeoStroke) */
            const penStrokesMap = realtime.get(pageId);
            const strokeKey = key;
            penStrokesMap.set(strokeKey, stroke);
        };
        /**
         * remove a stroke from 'realtime state' storage
         *
         * @private
         * @param {NeoStroke} stroke
         * @memberof InkStorage
         */
        this.removeFromRealtime = (stroke) => {
            const { section, owner, book, page, key } = stroke;
            const pageId = makeNPageIdStr({ section, owner, book, page });
            /** @type {Map.<string, Map>} - (pageId) ==> (strokeKey ==> NeoStroke) */
            const realtime = this.realtimeOnPage;
            if (realtime.has(pageId)) {
                /** @type {Map.<string, NeoStroke>} - (strokeKey) ==> (NeoStroke) */
                const penStrokesMap = realtime.get(pageId);
                const strokeKey = key;
                penStrokesMap.delete(strokeKey);
            }
            this.realtime[key] = null;
        };
        /**
         * create realtime stroke and make it open, and dispatch ON_PEN_DOWN event
         * after doing that, waiting for "appendDot", ..., "closeStroke"
         *
         * writerId = args.writerId;
         *
         * surfaceOwnerId = args.surfaceOwnerId ? surfaceOwnerId : (writerId ? writerId : undefined);
         *
         * @param {IOpenStrokeArg} args
         * @return {*}  {NeoStroke}
         * @memberof InkStorage
         */
        this.openStroke = (args) => {
            // let stroke = new NeoStroke(mac);
            // let stroke = initStroke(-1 /* section */, -1 /* owner */, -1 /*book */, -1 /* page */, time, mac);
            const { mac, time, thickness, brushType, color, inputType: deviceType, writerId, originalKey, // 2021/06/20
            // uuid, // 2022/04/06
             } = args;
            const surfaceOwnerId = args.surfaceOwnerId ? args.surfaceOwnerId : (writerId ? writerId : undefined);
            const strokeProps = {
                // uuid,
                mac, thickness, brushType, color, inputType: deviceType,
                startTime: time,
                originalKey,
                section: -1, owner: -1, book: -1, page: -1,
                status: brushType === IBrushType.ERASER ? StrokeStatus.ERASED : StrokeStatus.NORMAL,
                writerId,
                surfaceOwnerId,
            };
            const stroke = new NeoStroke(strokeProps);
            // 2021/06/10
            if (!this._surfaceOwnerIds.includes(surfaceOwnerId)) {
                this._surfaceOwnerIds.push(surfaceOwnerId);
                this.dispatcher.dispatch(InkStorageEventName.ON_OWNER_ADDED, {
                    inputType: stroke.inputType,
                    mac,
                    surfaceOwnerId
                });
            }
            if (!this._writerIds.includes(writerId)) {
                this._writerIds.push(writerId);
                this.dispatcher.dispatch(InkStorageEventName.ON_WRITER_ADDED, {
                    inputType: stroke.inputType,
                    mac,
                    writerId
                });
            }
            // stroke.thickness = thickness;     // kitty
            stroke.penTipMode = 0; // kitty
            // stroke.init(section, owner, book, page, startTime);
            // 펜 리스트에 추가, 획이 완성되고 나서 점검할까 했는데, 시작되는 지점에서 바로 넣기로 했다
            if (!this._penUIDs.includes(mac)) {
                this._penUIDs.push(mac);
                this.dispatcher.dispatch(InkStorageEventName.ON_PEN_ADDED, {
                    inputType: stroke.inputType,
                    mac,
                });
            }
            // 실시간 스트로크 관리 pool에 추가
            const strokeKey = stroke.key;
            if (this.realtime[strokeKey])
                this.realtime[strokeKey] = null;
            this.realtime[strokeKey] = stroke;
            // hand the event
            this.dispatcher.dispatch(PenEventName.ON_PEN_DOWN, { inputType: stroke.inputType, strokeKey, mac, time, stroke });
            // console.log(`DDD openStroke strokeKey = ${strokeKey}`)
            return stroke;
        };
        /**
         * attach realtime stroke to page and dispatch ON_PEN_PAGEINFO event
         *
         * @param {string} strokeKey
         * @param {number} section
         * @param {number} owner
         * @param {number} book
         * @param {number} page
         * @param {number} time
         * @memberof InkStorage
         */
        this.setStrokeInfo = (strokeKey, section, owner, book, page, time) => {
            const stroke = this.realtime[strokeKey];
            // console.log(`DDD setStrokeInfo strokeKey = ${strokeKey}`)
            stroke.section = section;
            stroke.owner = owner;
            stroke.book = book;
            stroke.page = page;
            const isOnPlate = isPlatePaper({ section, owner, book, page });
            this.addRealtimeToPage(stroke);
            if (stroke.brushType !== IBrushType.PUI && (!isOnPlate)) {
                // hand the event
                this.dispatcher.dispatch(PenEventName.ON_PEN_PAGEINFO, { inputType: stroke.inputType, strokeKey, mac: stroke.mac, stroke, section, owner, book, page, time });
            }
        };
        /**
         * get realtime stroke by strokeKey, if exists
         *
         * @param {string} strokeKey
         * @return {*}  {NeoStroke}
         * @memberof InkStorage
         */
        this.getRealTimeStroke = (strokeKey) => {
            /** @type {NeoStroke} */
            const stroke = this.realtime[strokeKey];
            if (!stroke) {
                console.error(`stroke was not initiated`);
                return null;
            }
            return stroke;
        };
        /**
         * add dot to the stroke created/opened and dispatch ON_PEN_MOVE event
         *
         * @param {string} strokeKey
         * @param {NeoDot} dot
         * @return {*}
         * @memberof InkStorage
         */
        this.appendDot = (strokeKey, dot) => {
            /** @type {NeoStroke} */
            const stroke = this.realtime[strokeKey];
            if (!stroke) {
                console.error(`stroke was not initiated`);
                console.error(dot);
                return;
            }
            // strokeAddDot(stroke, dot);
            stroke.addDot(dot);
            if (stroke.brushType !== IBrushType.PUI && (!isPlatePaper(stroke))) {
                // hand the event
                this.dispatcher.dispatch(PenEventName.ON_PEN_MOVE, { inputType: stroke.inputType, strokeKey, mac: stroke.mac, stroke, dot });
            }
        };
        /**
         * 2021/07/17 추가
         * 이걸 부르면, network에 추가되지 않을텐데...
         * @param stroke
         */
        this.closeStrokeWithoutEvent = (stroke, shouldNotMoveToCompleteSet = false) => {
            // closeStroke(stroke);
            stroke.close();
            if (!shouldNotMoveToCompleteSet) { // 2021/08/04 network 에서 들어오는 stroke 때문에, network 스트로크는 이게 true
                this.completed.set(stroke.key, stroke);
                // 2021/06/27
                if (stroke.originalKey)
                    this.originalKeyStrokes.set(stroke.originalKey, stroke);
                this.addCompletedToPage(stroke);
            }
            this.removeFromRealtime(stroke);
        };
        /**
         * close stroke to move to "completed" and dispatch ON_PEN_UP event
         *
         * @param {string} strokeKey
         * @memberof InkStorage
         */
        this.closeStroke = (strokeKey) => {
            /** @type {NeoStroke} */
            const stroke = this.realtime[strokeKey];
            if (!stroke) {
                console.error(`stroke was not initiated`);
                return;
            }
            const { mac, section, owner, book, page } = stroke;
            const isOnPlate = isPlatePaper({ section, owner, book, page });
            this.closeStrokeWithoutEvent(stroke, isOnPlate);
            // // closeStroke(stroke);
            // stroke.close();
            // this.completed.set(stroke.key, stroke);
            // // 2021/06/27
            // if (stroke.originalKey)
            //   this.originalKeyStrokes.set(stroke.originalKey, stroke);
            // this.addCompletedToPage(stroke);
            // this.removeFromRealtime(stroke);
            // hand the event
            // console.log( `inkStorage: close Stroke event dispatch`);
            if (!isOnPlate) {
                this.dispatcher.dispatch(PenEventName.ON_PEN_UP, {
                    inputType: stroke.inputType,
                    strokeKey,
                    mac,
                    stroke,
                    section,
                    owner,
                    book,
                    page,
                });
                this.dispatcher.dispatch(InkStorageEventName.ON_STROKE_ADDED, {
                    inputType: stroke.inputType,
                    strokeKey,
                    mac,
                    stroke,
                    section,
                    owner,
                    book,
                    page,
                });
            }
            return {
                inputType: stroke.inputType,
                strokeKey,
                mac,
                stroke,
                section,
                owner,
                book,
                page,
            };
            // console.log(this.completed.size)
        };
        /**
         * close stroke to move to "completed" and dispatch ON_PEN_UP event
         *
         * @param {string} strokeKey
         * @memberof InkStorage
         */
        this.closeStrokeNetwork = (strokeKey) => {
            const closeResult = this.closeStroke(strokeKey);
            const { inputType, stroke, mac, section, owner, book, page } = closeResult;
            // event
            this.dispatcher.dispatch(InkStorageEventName.ON_NETWORK_STROKE_ADDED, {
                stroke,
                strokeKey,
                inputType,
                mac,
                section, owner, book, page
            });
        };
        /**
         * 2021/06/09 추가
         *
         * @param {string} erasedStrokeKey
         * @param {IPageSOBP} sobp
         * @memberof InkStorage
         */
        this.markStrokeErased = (erasedStrokeKey, eraserStroke, sobp, inputType) => {
            const pageId = makeNPageIdStr(sobp);
            const pageStrokes = this.completedOnPage.get(pageId);
            const erasedStroke = pageStrokes === null || pageStrokes === void 0 ? void 0 : pageStrokes.find(ns => ns.key === erasedStrokeKey);
            if (erasedStroke) {
                this.markErased(erasedStroke);
                // 렌더러에 알린다
                this.dispatcher.dispatch(InkStorageEventName.ON_STROKE_ERASED, {
                    inputType: inputType ? inputType : NEO_SMARTPEN_TYPE.NETWORK_PEN,
                    erasedStrokes: [erasedStroke],
                    // eraserStroke: eraserStroke,
                });
                // TODO: 네트워크 센더에 알려야 하는 부분을 추가하자
                this.dispatcher.dispatch(InkStorageEventName.ON_STROKE_ERASED_TO_REMOTE, {
                    inputType: inputType ? inputType : NEO_SMARTPEN_TYPE.NETWORK_PEN,
                    erasedStrokes: [erasedStroke],
                    eraserStroke: eraserStroke,
                });
            }
        };
        this.markStrokeErasedWithOriginalKey = (originalKey, sobp, inputType) => {
            const pageId = makeNPageIdStr(sobp);
            // original key가 있는 stroke만
            const pageStrokes = this.completedOnPage.get(pageId).filter(stroke => !!stroke.originalKey);
            const erasedStroke = pageStrokes.find(ns => ns.originalKey === originalKey);
            if (erasedStroke) {
                this.markErased(erasedStroke);
                // 렌더러에 알린다
                this.dispatcher.dispatch(InkStorageEventName.ON_STROKE_ERASED, {
                    inputType: inputType ? inputType : NEO_SMARTPEN_TYPE.NETWORK_PEN,
                    erasedStrokes: [erasedStroke],
                });
            }
        };
        this.hasStroke = (strokeKey) => {
            return this.completed.has(strokeKey);
        };
        this.hasOriginalKeyStroke = (originalKey) => {
            return this.originalKeyStrokes.has(originalKey);
        };
        /**
         * page를 활성화하기 위해 dummy stroke를 추가한다
         * @param sobp
         */
        this.addDummyStrokeOnPage = (sobp, stroke) => {
            // const stroke = generateDummyStroke();
            // this.closeStroke(stroke.strokeKey);
        };
        this.fromInkStore = (pages, withReset = true) => {
            if (withReset)
                this.reset();
            for (const page of pages) {
                this.interpreteInkstoreData(page);
            }
            console.log("fromInkStore", pages);
            this.dispatcher.dispatch(InkStorageEventName.ON_INKSTORE_STROKE_LOADED, {
                inputType: NEO_SMARTPEN_TYPE.MENU_ACTION,
                strokeKey: "",
                mac: "",
                pageInfos: [],
                surfaceOwnerId: null,
                writerId: null,
            });
        };
        this.interpreteInkstoreData = (storedPage) => {
            const { section, owner, bookCode: book, pageNumber: page } = storedPage;
            const pageInfo = { section, owner, book, page };
            const pageInfoStr = makeNPageIdStr(pageInfo);
            // 스트로크 채워 넣기
            storedPage.strokes.forEach(stroke => {
                const neoStroke = NeoStroke.fromInkstoreStroke({ stroke, pageInfo });
                neoStroke.uploaded = true;
                if (!this.pageStartStroke.has(pageInfoStr) || this.pageStartStroke.get(pageInfoStr).startTime > neoStroke.startTime)
                    this.pageStartStroke.set(pageInfoStr, neoStroke);
                if (!this.pageEndStroke.has(pageInfoStr) || this.pageEndStroke.get(pageInfoStr).startTime < neoStroke.startTime)
                    this.pageEndStroke.set(pageInfoStr, neoStroke);
                this.addCompleteStrokeOnPageWithoutEventEmit(neoStroke);
            });
        };
        this.addCompleteStrokeOnPageWithoutEventEmit = (stroke) => {
            const { section, owner, book, page } = stroke;
            const pageId = makeNPageIdStr({ section, owner, book, page });
            // stroke에 점이 하나라도 있어야 옮긴다.
            if (stroke.dotArray.length > 0) {
                // 배열이 없으면 만들어 준다.
                if (!this.completedOnPage.has(pageId)) {
                    this.completedOnPage.set(pageId, new Array(0));
                }
                // 배열에 넣는다.
                /** @type {Array.<NeoStroke>} */
                const arr = this.completedOnPage.get(pageId);
                arr.push(stroke);
                // 2021/12/27
                this.strokes.push(stroke);
                this.lastPageInfo = { section, owner, book, page };
            }
        };
        this.reset = () => {
            this.strokes = [];
            this.completed = new Map(); // completed strokes
            this.originalKeyStrokes = new Map(); // originalKey completed strokes
            this.realtime = new Map(); // realtime strokes (incompleted strokes)
            /** (pageId (s.o.b.p)) ==> ({mac : NeoStroke[]}) */
            this.completedOnPage = new Map();
            this.pageStartStroke = new Map();
            this.pageEndStroke = new Map();
            /** (pageId) ==> ({strokeKey : NeoStroke}) */
            this.realtimeOnPage = new Map();
            this.lastPageInfo = { section: -1, book: -1, owner: -1, page: -1 };
            this._surfaceOwnerIds = [];
            this._writerIds = [];
            this._penUIDs = [];
        };
        this.name = name;
    }
    /**
     *
     */
    static getInstance() {
        if (_storage_instance)
            return _storage_instance;
        _storage_instance = new InkStorage("1st");
        return _storage_instance;
    }
    /**
     * 2021/07/06
     */
    static get2ndInstance() {
        if (!_storage_2nd_instance) {
            _storage_2nd_instance = new InkStorage("2nd");
        }
        return _storage_2nd_instance;
    }
}
//# sourceMappingURL=InkStorage.js.map